import { Button, Text } from '@lmn/design-system';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { IconArrowDownLinear } from '@/app/common/icons/libraryIcons';
import SoldOutAlternativeDates from '@/app/components/hotel-detail/SoldOutAlternativeDates';
import { EvoSectionContainer } from '@/app/components/hotel-detail/evo/EvoCommon';
import { useOffersContext } from '@/app/components/hotel-detail/OffersProvider';
import { useTranslation } from '@/app/components/translations/Translation';
import RoomPanelModal from '@/app/components/hotel-detail/rooms/RoomPanelModal';
import { EvoRoomCard } from '@/app/components/hotel-detail/evo/rooms/EvoRoomCard';
import { useSelectedMealPlan } from '@/app/components/hotel-detail/SelectedMealPlanProvider';
import { useTrackHotelShown } from '@/app/components/hotel-detail/hooks/useTrackHotelShown';
import { EvoLmnDealExtras } from '@/app/components/hotel-detail/evo/EvoLmnDealExtras';
import { useHotelPromotion } from '@/app/components/hotel-detail/hooks/useFetchHotelPromotions';
import { EvoBoardClassBar } from '@/app/components/hotel-detail/evo/EvoBoardClassBar';
import { selectBoardClassTarget } from '@/app/components/hotel-detail/evo/boardClassSelection';
import { MealPlanFilterMessage } from '@/app/components/hotel-detail/rooms/MealPlanFilterMessage';
import { pageSectionIds } from '@/app/components/hotel-detail/common/consts';
import { EvoRoomSkeletons } from '@/app/components/hotel-detail/evo/rooms/EvoRoomSkeletons';
import { EvoRoomsLoadingPanel } from '@/app/components/hotel-detail/evo/rooms/EvoRoomsLoadingPanel';
import { getEvoRoomCardItems } from '@/app/components/hotel-detail/evo/rooms/getEvoRoomCardItems';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { RoomsAd } from '@/app/common/biff/hotelDetailPage/RoomsAd.tsx';

const INITIAL_CARDS_SHOWN = 5;

const ShowMoreRoomsButton = styled(Button)`
  width: 100%;
`;

// TODO We need to take a decision on colored icons: cssFilters, lastminute-react-icons, cloudinary
const ChevronDownIcon = () => <IconArrowDownLinear aria-hidden="true" size={20} />;

export function EvoStayInfo(props: React.HTMLAttributes<HTMLDivElement>) {
  const { t } = useTranslation();
  const { offersData, isLoadingRooms, isSoldout } = useOffersContext();
  const { localization } = usePageWrapperData();
  const { datesOrigin } = useUrlSearchParams();
  const { selectedRoom, selectedMealPlan, setSelectedRoom, setSelectedMealPlan } = useSelectedMealPlan();

  // The "checking prices for your new dates" panel over the placeholders is for the reload a day picked
  // in the apex calendar triggers, not for the initial load or a date change made elsewhere (MPH-971).
  // `datesOrigin` outlives that reload, so it is combined with "no rooms held yet": the calendar resets
  // the offers when the dates change, whereas the later reloads that keep the rooms — a transport change,
  // a similar-hotel navigation — show the plain skeletons.
  const showApexDateChangePanel = isLoadingRooms && datesOrigin === 'APEX_CALENDAR' && !offersData?.rooms;
  const [selectedBoardClass, setSelectedBoardClass] = useState<BoardClassType | undefined>(
    selectedMealPlan?.boardClass
  );

  // The board class tab is anchored to the meal plan selected on the page: whenever the
  // selection moves to another board class (rate picked in a modal, default re-selected after
  // an apex calendar date change), the tab — and with it the rooms list — follows, so the list
  // always contains the selected offer.
  useEffect(() => {
    setSelectedBoardClass(selectedMealPlan?.boardClass);
  }, [selectedMealPlan?.boardClass]);

  // Picking a board class is a selection, not just a list filter: the page selection follows the
  // tab. The selected room is kept when it offers the new board class; otherwise the cheapest room
  // offering it is selected — in both cases with the cheapest rate of that board class. Clicking
  // the already-selected board class only re-filters, preserving any cancellation/bed choice.
  const onSelectBoardClass = useCallback(
    (boardClass: BoardClassType) => {
      setSelectedBoardClass(boardClass);
      if (boardClass === selectedMealPlan?.boardClass) return;

      const target = selectBoardClassTarget(offersData?.rooms ?? [], boardClass, selectedRoom);
      if (!target) return;
      setSelectedRoom(target.room);
      setSelectedMealPlan(target.mealPlan);
    },
    [offersData?.rooms, selectedRoom, selectedMealPlan?.boardClass, setSelectedRoom, setSelectedMealPlan]
  );

  // Show the deal extras card whenever the flash-sale promotion is active — the badge, explanation
  // and deal dates are useful on their own, and the card renders with LMN-deals or flash-sales
  // styling depending on the lastMinuteDeals config (MPH-744). The card self-hides the perks
  // section when there are no promotionAddedValues (see EvoLmnDealExtras).
  const { showFlashSalesPromotion } = useHotelPromotion();
  const showLmnDealExtras = showFlashSalesPromotion;

  useTrackHotelShown();

  // Room/option tracking (HOTEL_ROOM_CLICKED + HOTEL_ROOM_OPTION_CLICKED) is no longer fired on select
  // in the evo layout. Both events fire together when the user commits via "Continue" — see
  // useTrackEvoRoomSelection and its call sites (useEvoContinueFromRooms, the cancellation-type modal
  // and the room information modal).
  const [showAllRooms, setShowAllRooms] = useState(false);

  const cardItems = React.useMemo(
    () => getEvoRoomCardItems(offersData?.rooms, selectedBoardClass),
    [offersData?.rooms, selectedBoardClass]
  );

  const hasMoreCards = cardItems.length > INITIAL_CARDS_SHOWN;
  const visibleCardItems = showAllRooms ? cardItems : cardItems.slice(0, INITIAL_CARDS_SHOWN);

  // MPH-1089 / cux_4084: an ad slot between the first room option and the rest of the list. Cards and
  // slot go into one keyed array rather than separate children, because React matches keys only
  // within the same array child: split in two, a card moving across the boundary — a board class
  // change swaps which room comes first — would remount instead of move. Keyed, the slot keeps its
  // DOM node too, so googletag is not left holding an element that has left the page. That covers
  // re-renders; a rooms reload tears the whole list down, and RoomsAd refreshes its slot into the new node itself.
  const showRoomsAd = localization?.configValuesApp?.detailRoomsAdEnabled || false;
  const renderRoomCard = (item: (typeof visibleCardItems)[number]) => (
    <EvoRoomCard
      key={`${item.room.roomOfferId}-${item.promotionKey}`}
      room={item.room}
      promotionKey={item.promotionKey}
      selectedBoardClass={selectedBoardClass}
    />
  );

  return (
    <EvoSectionContainer {...props} id={pageSectionIds.rooms}>
      {isSoldout ? (
        <SoldOutAlternativeDates />
      ) : !isLoadingRooms && offersData?.rooms && offersData.rooms.length > 0 ? (
        <>
          <div
            css={`
              display: flex;
              flex-direction: column;
            `}
          >
            {showLmnDealExtras ? (
              <EvoLmnDealExtras
                css={`
                  margin-bottom: 32px;
                `}
              />
            ) : undefined}

            <Text
              variant="Title2"
              css={`
                margin-bottom: 24px;
              `}
            >
              {t('vacation.select.section.rooms.title')}
            </Text>
            {offersData?.roomsFiltered && (
              <MealPlanFilterMessage
                css={`
                  margin-bottom: 24px;
                `}
              />
            )}

            <EvoBoardClassBar
              rooms={offersData.rooms}
              onSelectBoardClass={onSelectBoardClass}
              initialBoardClass={selectedMealPlan?.boardClass}
              css={`
                margin-bottom: 16px;
              `}
            />
            <div
              css={`
                display: flex;
                flex-direction: column;
                row-gap: 16px;
              `}
            >
              {[
                ...visibleCardItems.slice(0, 1).map(renderRoomCard),
                showRoomsAd && visibleCardItems.length > 0 ? <RoomsAd key="rooms-ad" /> : null,
                ...visibleCardItems.slice(1).map(renderRoomCard),
              ]}
              {hasMoreCards && !showAllRooms && (
                <ShowMoreRoomsButton
                  variant="secondary"
                  appearance="outlined"
                  size="large"
                  iconTrailing={ChevronDownIcon}
                  onClick={() => setShowAllRooms(true)}
                  data-testid="evo-show-more-rooms"
                >
                  {t('vacation.select.cc.rooms.see_more_rooms')}
                </ShowMoreRoomsButton>
              )}
            </div>
          </div>
          {offersData?.rooms && offersData.rooms.length > 0 ? <RoomPanelModal /> : undefined}
        </>
      ) : (
        <>
          <Text variant="Title2">{t('vacation.select.section.rooms.title')}</Text>
          {/* position: relative anchors the loading panel's layers over the bar and the skeletons. */}
          <div
            css={`
              position: relative;
              display: flex;
              flex-direction: column;
              row-gap: 16px;
            `}
          >
            {/* No separator here: the loaded branch has none between the bar and the cards,
                so one would pop out of existence when the rooms land. */}
            <EvoBoardClassBar rooms={[]} onSelectBoardClass={() => {}} />
            <EvoRoomSkeletons />
            {showApexDateChangePanel && <EvoRoomsLoadingPanel />}
          </div>
        </>
      )}
    </EvoSectionContainer>
  );
}
